import { FC, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import LoadingSpinner from "src/components/reusable/LoadingSpinner";
import { DashboardExportButton, DashboardSectionHeader, DashboardNarrativePanel } from "src/components/common";
import { useDashboardNarrative } from "src/hooks/UseDashboardNarrative";
import {
  useInstagramFollowersReport,
  type FollowersTrendGranularity,
} from "../../../hooks/UseOrganicMediaData";
import OrganicDateRangePicker from "src/components/common/OrganicDateRangePicker/OrganicDateRangePicker";
import { normalizeApiLang } from "src/utils/apiLang";
import InstagramFollowersKpiCards from "./InstagramFollowersKpiCards";
import InstagramNewFollowers from "./InstagramNewFollowers";
import InstagramGenderDemographics from "./InstagramGenderDemographics";
import InstagramFollowersByCountry from "./InstagramFollowersByCountry";
import InstagramFollowersByCity from "./InstagramFollowersByCity";
import "../../../styles/InstagramFollowersReport/InstagramFollowersReport.scss";

interface InstagramAgeBreakdown {
  range: string;
  count: number;
}

const DEFAULT_AGE_BUCKETS: InstagramAgeBreakdown[] = [
  { range: "13-17", count: 0 },
  { range: "18-24", count: 0 },
  { range: "25-34", count: 0 },
  { range: "35-44", count: 0 },
  { range: "45-54", count: 0 },
  { range: "55-64", count: 0 },
  { range: "65+", count: 0 },
];

const AGE_COLORS = [
  "#2f6df2",
  "#31cdbc",
  "#a855f7",
  "#8b5cf6",
  "#5ca6f8",
  "#16b981",
  "#f59e0b",
];

const GENDER_ORDER = ["male", "female", "unknown"];

interface InstagramFollowersReportProps {
  dateFilters: import("../FacebookContentReport").OrganicDateFilters;
  instagramTopCountriesCount: number | undefined;
  instagramTopCitiesCount: number | undefined;
  /* eslint-disable-next-line no-unused-vars */
  setInstagramTopCountriesCount: (value: number | undefined) => void;
  /* eslint-disable-next-line no-unused-vars */
  setInstagramTopCitiesCount: (value: number | undefined) => void;
  todayISO: string;
}

const MAX_TOP = 10;

const parseTrendDateMs = (value: string) => {
  const isoMatch = String(value ?? "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    return Date.UTC(Number(isoMatch[1]), Number(isoMatch[2]) - 1, Number(isoMatch[3]));
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? Number.NaN : parsed.getTime();
};

const formatComparisonLabel = (label: string | undefined, t: (_key: string) => string) => {
  const trimmed = (label ?? "").trim();
  const normalized = trimmed.toLowerCase();
  if (
    !normalized ||
    normalized === "vs last period" ||
    normalized === "vs previous period" ||
    trimmed === "مقارنة بالفترة السابقة"
  ) {
    return t("organicMedia.tooltips.vsPreviousPeriod").replace(/:$/, "");
  }
  return label as string;
};

const formatGenderTitle = (key: string, t: (_key: string, _options?: Record<string, unknown>) => string) =>
  t(`organicMedia.followers.gender.${key || "unknown"}`, {
    defaultValue: key ? `${key.charAt(0).toUpperCase()}${key.slice(1)}` : "Unknown",
  });

const InstagramFollowersReport: FC<InstagramFollowersReportProps> = ({
  dateFilters,
  instagramTopCountriesCount,
  instagramTopCitiesCount,
  setInstagramTopCountriesCount,
  setInstagramTopCitiesCount,
  todayISO,
}) => {
  const { t, i18n } = useTranslation();
  const apiLang = normalizeApiLang(i18n.language);
  const [trendGranularity, setTrendGranularity] =
    useState<FollowersTrendGranularity>("daily");
  const pdfContainerRef = useRef<HTMLDivElement>(null);
  const {
    appliedStartDate,
    appliedEndDate,
    draftStartDate,
    draftEndDate,
    onApplyDates,
  } = dateFilters;
  const {
    kpis: instagramKpis,
    genderDemographics,
    countries,
    cities,
    followerTrend,
    loading,
    error,
  } = useInstagramFollowersReport(appliedStartDate, appliedEndDate, apiLang, trendGranularity);

  const igFollowersNarrativePayload = useMemo<Record<string, unknown>>(
    () => ({
      kpis: instagramKpis,
      gender_demographics: genderDemographics,
      countries,
      cities,
      follower_trend: { daily: followerTrend, monthly: [] },
    }),
    [instagramKpis, genderDemographics, countries, cities, followerTrend],
  );

  const { narrative: igFollowersNarrative, loading: igFollowersNarrativeLoading, aiSummary } = useDashboardNarrative({
    dashboardKey: "organic_media",
    tabKey: "instagram_followers_report",
    startDate: appliedStartDate,
    endDate: appliedEndDate,
    data: igFollowersNarrativePayload,
    promptVersion: "v6",
    enabled: !loading && !error && Boolean(instagramKpis.length),
  });

  const countriesSliderMax = MAX_TOP;
  const citiesSliderMax = MAX_TOP;
  const defaultCountriesCount = Math.max(
    1,
    Math.min(countries.length || 1, MAX_TOP)
  );
  const defaultCitiesCount = Math.max(1, Math.min(cities.length || 1, MAX_TOP));
  const countriesSliderValue = Math.min(
    countriesSliderMax,
    Math.max(1, instagramTopCountriesCount ?? defaultCountriesCount),
  );
  const citiesSliderValue = Math.min(
    citiesSliderMax,
    Math.max(1, instagramTopCitiesCount ?? defaultCitiesCount),
  );

  const trendChartData = useMemo(() => {
    const sortedTrend = [...followerTrend].sort((left, right) => {
      const leftMs = parseTrendDateMs(left.date);
      const rightMs = parseTrendDateMs(right.date);
      if (Number.isNaN(leftMs) || Number.isNaN(rightMs)) {
        return left.date.localeCompare(right.date);
      }
      return leftMs - rightMs;
    });

    if (sortedTrend.length === 0) {
      return [];
    }

    return sortedTrend.map(point => ({
      date: point.date,
      value: Number(point.value ?? 0),
      valueFormattedValue: point.valueFormattedValue,
    }));
  }, [followerTrend]);

  const genderCards = useMemo(() => {
    const genderMap = new Map<string, (typeof genderDemographics)[number]>(
      genderDemographics.map(group => [group.key, group])
    );
    const orderedKeys = [
      ...GENDER_ORDER,
      ...genderDemographics
        .map(group => group.key)
        .filter(key => !GENDER_ORDER.includes(key)),
    ];

    return orderedKeys.map(key => {
      const group = genderMap.get(key);
      const ages = group?.ages?.length ? group.ages : DEFAULT_AGE_BUCKETS;
      const totalAgeCount = ages.reduce((sum, age) => sum + Number(age.count ?? 0), 0);
      const donutData = ages.map((age, index) => ({
        name: age.range,
        value: totalAgeCount > 0 ? Number(age.count ?? 0) : 1,
        count: Number(age.count ?? 0),
        percent: totalAgeCount > 0 ? (Number(age.count ?? 0) / totalAgeCount) * 100 : 0,
        color: AGE_COLORS[index % AGE_COLORS.length],
      }));

      return {
        key,
        title: formatGenderTitle(key, t),
        percent: group?.percent ?? 0,
        comparisonLabel: formatComparisonLabel(group?.comparisonLabel, t),
        donutData,
      };
    });
  }, [genderDemographics, t]);

  return (
    <div ref={pdfContainerRef}>
      <DashboardSectionHeader
        className="organic-content-report__section-header"
        title={t("organicMedia.tabs.instagramFollowers")}
        subtitle={t("organicMedia.followersReport.instagramPageSubtitle")}
        actionsClassName="dashboard-section-header__actions--filters"
        actions={
          <>
            <div className="dashboard-section-header__control dashboard-section-header__control--wide">
              <OrganicDateRangePicker
                startDate={draftStartDate}
                endDate={draftEndDate}
                maxDate={todayISO}
                onRangeChange={onApplyDates}
              />
            </div>
            <DashboardExportButton
              dashboardName="Instagram Followers"
              dateRange={{ startDate: appliedStartDate, endDate: appliedEndDate }}
              containerRef={pdfContainerRef}
              disabled={loading || Boolean(error)}
            />
          </>
        }
      />

      {error && (
        <div className="organic-tabs-dashboard__error" role="alert">
          {error}
        </div>
      )}

      <div data-pdf-block="narrative">
        <DashboardNarrativePanel
          narrative={igFollowersNarrative}
          loading={loading || igFollowersNarrativeLoading}
          aiSummary={aiSummary}
        />
      </div>

      {loading ? (
        <LoadingSpinner compact />
      ) : (
        <div className="organic-instagram-layout">
          <div className="organic-instagram-layout__top" data-pdf-block="followers-overview">
            <InstagramFollowersKpiCards kpis={instagramKpis} />
            <InstagramNewFollowers
              trendChartData={trendChartData}
              trendGranularity={trendGranularity}
              onTrendGranularityChange={setTrendGranularity}
            />
          </div>

          <InstagramGenderDemographics genderCards={genderCards} />

          <div className="organic-instagram-layout__geo" data-pdf-block="followers-geo">
            <InstagramFollowersByCountry
              rows={countries}
              value={countriesSliderValue}
              max={countriesSliderMax}
              onChange={setInstagramTopCountriesCount}
            />
            <InstagramFollowersByCity
              rows={cities}
              value={citiesSliderValue}
              max={citiesSliderMax}
              onChange={setInstagramTopCitiesCount}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default InstagramFollowersReport;
